package com.flowyride.data.favorites

import com.flowyride.core.api.ApiErrorDto
import com.flowyride.core.api.FavoriteDto
import com.flowyride.core.api.FavoriteSummaryDto
import com.flowyride.core.api.FavoritesApi
import com.flowyride.core.api.RenameFavoriteDto
import com.flowyride.core.api.toDomain
import com.flowyride.core.api.toDto
import com.flowyride.core.api.toFavoriteException
import com.flowyride.core.domain.favorite.Favorite
import com.flowyride.core.domain.favorite.FavoriteException
import com.flowyride.core.domain.favorite.FavoriteRepository
import com.flowyride.core.domain.favorite.FavoriteSummary
import com.flowyride.core.domain.favorite.NewFavorite
import com.flowyride.core.network.NetworkJson
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlin.coroutines.cancellation.CancellationException

/**
 * Favorites stored on the FlowyRide server, on the signed-in rider's account.
 *
 * @param client a client carrying the session (see `withServerSession`).
 */
class HttpFavoriteRepository(
    private val client: HttpClient,
    baseUrl: String,
) : FavoriteRepository {

    private val baseUrl = baseUrl.trimEnd('/')

    override suspend fun list(): List<FavoriteSummary> = favoriteCall {
        client.get(baseUrl + FavoritesApi.PATH).ensureSuccess().body<List<FavoriteSummaryDto>>().map { it.toDomain() }
    }

    override suspend fun get(id: Long): Favorite = favoriteCall {
        client.get(baseUrl + FavoritesApi.item(id)).ensureSuccess().body<FavoriteDto>().toDomain()
    }

    override suspend fun save(favorite: NewFavorite): FavoriteSummary = favoriteCall {
        client.post(baseUrl + FavoritesApi.PATH) {
            contentType(ContentType.Application.Json)
            setBody(favorite.toDto())
        }.ensureSuccess().body<FavoriteSummaryDto>().toDomain()
    }

    override suspend fun rename(id: Long, name: String): FavoriteSummary = favoriteCall {
        client.patch(baseUrl + FavoritesApi.item(id)) {
            contentType(ContentType.Application.Json)
            setBody(RenameFavoriteDto(name))
        }.ensureSuccess().body<FavoriteSummaryDto>().toDomain()
    }

    override suspend fun delete(id: Long) {
        favoriteCall { client.delete(baseUrl + FavoritesApi.item(id)).ensureSuccess() }
    }
}

/** Normalises any failure of a server call into a [FavoriteException]. */
internal inline fun <T> favoriteCall(block: () -> T): T =
    try {
        block()
    } catch (e: CancellationException) {
        throw e
    } catch (e: FavoriteException) {
        throw e
    } catch (e: Throwable) {
        // Throwable: on the web, Ktor reports network failures as kotlin.Error ("Fail to fetch").
        throw FavoriteException.Unreachable(e)
    }

internal suspend fun HttpResponse.ensureSuccess(): HttpResponse {
    if (status.isSuccess()) return this
    val error = runCatching { NetworkJson.decodeFromString<ApiErrorDto>(bodyAsText()) }.getOrNull()
    throw error?.toFavoriteException() ?: FavoriteException.Unreachable(IllegalStateException("HTTP ${status.value}"))
}
